// ═══════════════════════════════════════════════════
//  Upholstery design behavior traits
// ═══════════════════════════════════════════════════
//
// What a design key *does*, separated from what it is called.
//
// Until this module existed the answer lived in seventeen hardcoded eight-key
// structures — two shared resolvers, three const arrays duplicated across four
// selectors, nine cost helpers, four labor ternaries and two admin pages. That
// is why adding a ninth design was a code change: every one of those sites had
// to learn the new literal.
//
// The catalog row now carries these four fields, and every branch reads them
// through resolveDesignTraits. SEEDED_DESIGN_TRAITS is the fallback, and it is
// transcribed from the behavior those literals had on 2026-08-21 — so a call
// site that has not yet been handed catalog rows still produces exactly today's
// numbers for the eight seeded designs.

export type ChannelAxis = 'run' | 'cross';

export interface UpholsteryDesignTraits {
  /** Channels are counted and the channel-width inputs render. */
  has_channels: boolean;
  /**
   * Which dimension the channels divide. `run` is the section width for the
   * base and both cushions and the depth for an arm; `cross` is the profile
   * height. Null whenever has_channels is false.
   */
  channel_axis: ChannelAxis | null;
  /**
   * Channels carry tufting wire. Gates the wire hardware input, the wire cost
   * line, the per-channel fabric allowance, and the channel labor minutes.
   *
   * Deliberately separate from has_channels: `channel_seam` counts channels and
   * shows the channel-width input but earns none of those four.
   */
  is_tufted: boolean;
  /** Buttons are counted; the tuftings-per-foot and button hardware inputs render. */
  has_buttons: boolean;
}

/** What an unrecognized key gets: the behavior `plain` has always had. */
export const INERT_DESIGN_TRAITS: UpholsteryDesignTraits = Object.freeze({
  has_channels: false,
  channel_axis: null,
  is_tufted: false,
  has_buttons: false,
});

/**
 * The eight seeded designs, transcribed from their real branch sites.
 *
 * Read off, not inferred:
 *  - has_channels  <- resolveChannelCount's type test and the selectors' CHANNEL_TYPES
 *  - channel_axis  <- which dimension resolveChannelCount divides
 *  - is_tufted     <- the selectors' TUFTED_CHANNEL_TYPES, the wire branch in the
 *                     cost helpers, resolveDesignTypeAllowancePct's per_channel
 *                     test, and labor-cost.service's orientation ternaries
 *  - has_buttons   <- resolveButtonCount's type test and the selectors' BUTTON_TYPES
 */
export const SEEDED_DESIGN_TRAITS: Readonly<Record<string, UpholsteryDesignTraits>> = Object.freeze({
  plain:                     { has_channels: false, channel_axis: null,    is_tufted: false, has_buttons: false },
  vertical_channel_tufted:   { has_channels: true,  channel_axis: 'run',   is_tufted: true,  has_buttons: false },
  horizontal_channel_tufted: { has_channels: true,  channel_axis: 'cross', is_tufted: true,  has_buttons: false },
  diamond_button_tufted:     { has_channels: false, channel_axis: null,    is_tufted: false, has_buttons: true },
  biscuit_button_tufted:     { has_channels: false, channel_axis: null,    is_tufted: false, has_buttons: true },
  head_roll:                 { has_channels: false, channel_axis: null,    is_tufted: false, has_buttons: false },
  channel_seam:              { has_channels: true,  channel_axis: 'run',   is_tufted: false, has_buttons: false },
  diamond_seam:              { has_channels: false, channel_axis: null,    is_tufted: false, has_buttons: false },
});

/** The shape resolveDesignTraits needs off a catalog row. Structural rather
 *  than the full UpholsteryDesignOption so callers can pass their own row type
 *  without a cast. */
export interface DesignTraitSource {
  key: string;
  has_channels?: boolean | null;
  channel_axis?: ChannelAxis | string | null;
  is_tufted?: boolean | null;
  has_buttons?: boolean | null;
}

function normalizeAxis(raw: ChannelAxis | string | null | undefined): ChannelAxis | null {
  return raw === 'run' || raw === 'cross' ? raw : null;
}

/**
 * Traits for one design key.
 *
 * Precedence: the catalog row, then the seeded map, then inert.
 *
 * The seeded fallback is what makes this migration safe to land in pieces. A
 * cost helper that has not yet been threaded with catalog rows calls this with
 * `rows` undefined and still gets the right answer for all eight existing
 * designs — so no intermediate commit can mis-price one of them.
 *
 * A seeded key absent from a partially-loaded catalog also falls back rather
 * than going inert, which is why the seeded lookup is a second chance and not
 * an else-branch of `rows == null`.
 */
export function resolveDesignTraits(
  key: string | null | undefined,
  rows?: readonly DesignTraitSource[] | null,
): UpholsteryDesignTraits {
  if (!key) return INERT_DESIGN_TRAITS;

  const row = rows?.find((r) => r.key === key);
  if (row) {
    return {
      has_channels: row.has_channels === true,
      channel_axis: normalizeAxis(row.channel_axis),
      is_tufted: row.is_tufted === true,
      has_buttons: row.has_buttons === true,
    };
  }

  return SEEDED_DESIGN_TRAITS[key] ?? INERT_DESIGN_TRAITS;
}

/**
 * Tufting wire length for one component, in linear feet.
 *
 * Wire runs across the axis the channels do *not* divide: vertical channels
 * divide the run and the wire runs down the cross axis; horizontal channels
 * divide the cross axis and the wire runs along the run. Nine cost helpers
 * each carried their own copy of this with the axis picked by a string literal.
 *
 * Returns 0 for a design that is not tufted, so callers no longer need their
 * own type test — the trait is the gate.
 */
export function resolveWireLinearFeet(
  channelsPerSection: number,
  traits: UpholsteryDesignTraits,
  runInches: number,
  crossInches: number,
  sectionCount: number,
): number {
  if (!traits.is_tufted || channelsPerSection <= 0) return 0;
  if (traits.channel_axis === 'run') return channelsPerSection * (crossInches / 12) * sectionCount;
  if (traits.channel_axis === 'cross') return channelsPerSection * (runInches / 12) * sectionCount;
  // is_tufted with no axis is a malformed catalog row. Zero is the safe answer:
  // it under-charges rather than inventing a dimension.
  return 0;
}
